import { type ComputedAddOn } from "./addons"
import { type EditorSurface, type RoomProduct, type WasteMode } from "./document"
import { addOnItemLabel, unitLabel } from "./labels"
import { productInk } from "./overlay"
import { cartonsFor } from "./patterns"
import { type ProductAssignment, rollGood } from "./seams"
import { productLabel } from "./rollup"

// The order worksheet's line model: FLAT records plus pivots, never a nested
// tree (teardown 5.7 — the tree is the intuitive-but-wrong shape). Grain is
// the product (style · colour · format); rooms are an expandable CHILD level
// of a line, and the by-room view regroups the same flat records on the fly.
//
// Quantities only — no cost, price, margin-the-money-kind, tax, or total-$.
// Pure and dependency-free (vitest-covered): the editor's Takeoff panel and
// the printed page both call this module, so they cannot disagree.

/**
 * The structural slice of a measured room this module needs — the editor's
 * ComputedRoom satisfies it, and the printed page builds it from the same
 * computeAllRooms call.
 */
export type WorksheetRoomInput = {
  id: string
  name: string
  product: RoomProduct | null
  /** User margin, "+% of net". */
  waste: number
  multiplier: number
  mode: WasteMode
  /** Net SF per copy; null on an uncalibrated sheet. */
  sqft: number | null
  /** The ordering quantity (multiplier + margin applied), SF. */
  order: number | null
  /** Seam-derived layout waste, SF (multiplier included); null without a plan. */
  computedWasteSf: number | null
}

export type WorksheetRoomDetail = {
  rowId: string
  label: string
  /** In the line's display unit — unless `unit` overrides it. */
  netQty: number | null
  orderQty: number | null
  /** Which waste model made orderQty; null for typed rows. */
  mode: WasteMode | null
  marginPct: number | null
  multiplier: number
  /** Typed, not measured (surface manual_qty / manual add-on). */
  manual: boolean
  /**
   * Set when this detail's quantities are in a DIFFERENT unit than the line
   * (an each/linear-unit surface typed under an area line) — such details
   * never fold into the line's totals, because "2 EA" added to square feet
   * is the silent relabeling this module exists to prevent.
   */
  unit: string | null
}

export type WorksheetLine = {
  kind: "product" | "addon"
  key: string
  /** Rule-derived add-on lines nest under their product's line. */
  parentKey: string | null
  label: string
  color: string | null
  productType: string | null
  /** Display unit — SY when the catalog says SY, the item's own for add-ons. */
  unit: string
  rooms: WorksheetRoomDetail[]
  roomCount: number
  netQty: number | null
  /** Engine-computed layout waste, display unit — separate from margin. */
  computedWasteQty: number | null
  /** The user margin, uniform across the line's rooms or "mixed". */
  marginPct: number | "mixed" | null
  orderQty: number | null
  cartons: number | null
  rolls: number | null
  lf: number | null
  /** The trust column: which waste model stands behind orderQty. */
  mode: WasteMode | "mixed" | null
  manual: boolean
  minApplied: boolean
  zeroRows: ComputedAddOn["zeroRows"]
  unmeasured: string[]
  incompatible: boolean
}

export type WorksheetResult = {
  lines: WorksheetLine[]
  /** Rooms with no product — visible, or the totals read as complete. */
  unassignedRooms: string[]
}

/**
 * The unit a product's line displays and totals in. SY products show SY
 * (SF / 9); every other area-class (or unit-less) product shows SF —
 * measured geometry is square feet, so an each/linear catalog unit cannot
 * relabel a drawn room's area. A line with ONLY typed surfaces and a
 * non-area unit keeps that unit verbatim (a "2 EA" medallion line is 2 EA).
 */
function areaDisplay(product: RoomProduct): { unit: string; factor: number } {
  return product.unit?.trim().toUpperCase() === "SY" ? { unit: "SY", factor: 1 / 9 } : { unit: "SF", factor: 1 }
}

function areaClassUnit(product: RoomProduct): boolean {
  const u = product.unit?.trim().toUpperCase()
  return !u || u === "SF" || u === "SY"
}

export function worksheetLines(args: {
  rooms: WorksheetRoomInput[]
  surfaces: EditorSurface[]
  addOns: ComputedAddOn[]
  assignments: Record<string, ProductAssignment>
}): WorksheetResult {
  const unassignedRooms: string[] = []
  type Accum = {
    product: RoomProduct
    rooms: WorksheetRoomDetail[]
    /** Typed surface rows, materialized at build time once the unit is known. */
    surfaces: { rowId: string; label: string; qty: number | null }[]
    netSf: number
    orderSf: number
    computedWasteSf: number
    hasComputedWaste: boolean
    margins: Set<number>
    modes: Set<WasteMode>
    manual: boolean
    unmeasured: string[]
  }
  const byProduct = new Map<string, Accum>()

  const accumFor = (product: RoomProduct): Accum => {
    let entry = byProduct.get(product.id)
    if (!entry) {
      entry = {
        product,
        rooms: [],
        surfaces: [],
        netSf: 0,
        orderSf: 0,
        computedWasteSf: 0,
        hasComputedWaste: false,
        margins: new Set(),
        modes: new Set(),
        manual: false,
        unmeasured: [],
      }
      byProduct.set(product.id, entry)
    }
    return entry
  }

  for (const room of args.rooms) {
    if (!room.product) {
      unassignedRooms.push(room.name)
      continue
    }
    const entry = accumFor(room.product)
    const { factor } = areaDisplay(room.product)
    const netSf = room.sqft == null ? null : room.sqft * Math.max(room.multiplier, 1)
    entry.rooms.push({
      rowId: room.id,
      label: room.name,
      netQty: netSf == null ? null : netSf * factor,
      orderQty: room.order == null ? null : room.order * factor,
      mode: room.mode,
      marginPct: room.waste,
      multiplier: Math.max(room.multiplier, 1),
      manual: false,
      unit: null,
    })
    if (netSf == null) entry.unmeasured.push(room.name)
    else entry.netSf += netSf
    if (room.order != null) entry.orderSf += room.order
    if (room.computedWasteSf != null) {
      entry.computedWasteSf += room.computedWasteSf
      entry.hasComputedWaste = true
    }
    entry.margins.add(room.waste)
    entry.modes.add(room.mode)
  }

  // A surface's typed quantity joins its product's line as a manual detail.
  // It is typed in the PRODUCT'S OWN unit, so it is held raw here and folded
  // in at build time, once the line's display unit is known — "2 EA" of
  // trim must never silently total as 2 square feet. A qty-less surface
  // still lists (visible blank — wall tile can't be invisible again), and
  // may CREATE the line: shower tile often appears on no room.
  for (const surface of args.surfaces) {
    if (!surface.product) continue
    const entry = accumFor(surface.product)
    entry.surfaces.push({ rowId: surface.id, label: surface.name, qty: surface.manualQty })
    if (surface.manualQty != null) entry.manual = true
  }

  const productLines: WorksheetLine[] = [...byProduct.values()]
    .map((entry) => {
      const areaUnit = areaClassUnit(entry.product)
      // A drawn room's measured area is square feet whatever the catalog
      // unit says; only a surfaces-only line may keep a non-area unit.
      const lineIsArea = areaUnit || entry.rooms.length > 0
      const { unit, factor } = lineIsArea
        ? areaDisplay(entry.product)
        : { unit: unitLabel(entry.product.unit), factor: 1 }

      let typedTotal = 0
      const surfaceDetails: WorksheetRoomDetail[] = entry.surfaces.map((s) => {
        const foldable = s.qty != null && (areaUnit || !lineIsArea)
        if (s.qty != null && areaUnit && lineIsArea) {
          // Typed in the display unit — normalize to SF for the accumulators.
          entry.netSf += s.qty / factor
          entry.orderSf += s.qty / factor
        }
        if (s.qty != null && !lineIsArea) typedTotal += s.qty
        return {
          rowId: s.rowId,
          label: s.label,
          netQty: s.qty,
          orderQty: s.qty,
          mode: null,
          marginPct: null,
          multiplier: 1,
          manual: true,
          // A typed EA/LF figure under an SF line keeps its own unit and
          // stays OUT of the totals — never silently relabeled.
          unit: foldable ? null : unitLabel(entry.product.unit),
        }
      })

      const rooms = [...entry.rooms, ...surfaceDetails]
      const assignment = args.assignments[entry.product.id]
      const roll = rollGood(entry.product)
      const margins = [...entry.margins]
      return {
        kind: "product" as const,
        key: entry.product.id,
        parentKey: null,
        label: productLabel(entry.product),
        color: productInk(entry.product),
        productType: entry.product.product_type,
        unit,
        rooms,
        roomCount: rooms.length,
        netQty: lineIsArea ? entry.netSf * factor : typedTotal,
        computedWasteQty:
          lineIsArea && entry.hasComputedWaste ? entry.computedWasteSf * factor : null,
        marginPct:
          margins.length > 1 ? ("mixed" as const) : (margins[0] ?? null),
        orderQty: lineIsArea ? entry.orderSf * factor : typedTotal,
        cartons:
          lineIsArea && !roll ? cartonsFor(entry.orderSf, entry.product.sqft_per_carton) : null,
        rolls: assignment ? assignment.rolls.length : null,
        lf: assignment ? assignment.totalLf : null,
        mode:
          entry.modes.size > 1
            ? ("mixed" as const)
            : (entry.modes.values().next().value ?? null),
        manual: entry.manual,
        minApplied: false,
        zeroRows: [],
        unmeasured: entry.unmeasured,
        incompatible: false,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  const addOnLine = (computed: ComputedAddOn): WorksheetLine => ({
    kind: "addon",
    key: computed.key,
    parentKey:
      computed.source.kind === "rule" && byProduct.has(computed.source.rule.productId)
        ? computed.source.rule.productId
        : null,
    label: addOnItemLabel(computed.item),
    color: null,
    productType: computed.item.source === "fc_replica" ? computed.item.product_type : null,
    unit: unitLabel(computed.unit),
    rooms: computed.contributions.map((c) => ({
      rowId: c.rowId ?? "",
      label: c.label,
      netQty: null,
      orderQty: c.qty,
      mode: null,
      marginPct: null,
      multiplier: 1,
      manual: computed.calc.calc_method === "manual" || computed.calc.calc_method === "count",
      unit: null,
    })),
    roomCount: computed.contributions.length,
    netQty: null,
    computedWasteQty: null,
    marginPct: null,
    orderQty: computed.qty,
    cartons: null,
    rolls: null,
    lf: null,
    mode: null,
    manual: computed.calc.calc_method === "manual",
    minApplied: computed.minApplied,
    zeroRows: computed.zeroRows,
    unmeasured: computed.unmeasured,
    incompatible: computed.qty == null,
  })

  // Render order: each product line, then the rule lines nested under it;
  // takeoff add-ons (room / surface / sheet scope) follow as their own
  // section, in the estimator's own order.
  const ruleLines = args.addOns.filter((a) => a.source.kind === "rule").map(addOnLine)
  const takeoffLines = args.addOns.filter((a) => a.source.kind === "addon").map(addOnLine)

  const lines: WorksheetLine[] = []
  for (const product of productLines) {
    lines.push(product)
    for (const rule of ruleLines) if (rule.parentKey === product.key) lines.push(rule)
  }
  // A rule whose product is on no room still shows (orphaned policy is
  // information, not noise) — after the products, before the takeoff section.
  for (const rule of ruleLines) if (rule.parentKey === null) lines.push(rule)
  lines.push(...takeoffLines)

  return { lines, unassignedRooms }
}

/** One room's (or surface's) slice of the takeoff, for the by-room view. */
export type WorksheetRoomGroup = {
  rowId: string | null
  label: string
  entries: {
    key: string
    kind: WorksheetLine["kind"]
    label: string
    unit: string
    netQty: number | null
    orderQty: number | null
    mode: WasteMode | null
    manual: boolean
  }[]
}

/**
 * The same flat lines regrouped by room — grain × scope as two orthogonal
 * controls, exactly the pivot the teardown recommends (5.3), computed on the
 * fly rather than stored. Lines with no per-room contribution (typed sheet
 * extras) land in a trailing "Sheet & takeoff-wide" group.
 *
 * `rowOrder` is the schedule's own row order (rooms then surfaces, by
 * position) — without it groups would fall out in product-label order, which
 * reads as random to someone walking the plan top to bottom.
 */
export function byRoomPivot(lines: WorksheetLine[], rowOrder: string[] = []): WorksheetRoomGroup[] {
  const groups = new Map<string, WorksheetRoomGroup>()
  const order: string[] = []
  const groupFor = (rowId: string | null, label: string): WorksheetRoomGroup => {
    const key = rowId ?? "__flat__"
    let group = groups.get(key)
    if (!group) {
      group = { rowId, label: rowId ? label : "Sheet & takeoff-wide", entries: [] }
      groups.set(key, group)
      order.push(key)
    }
    return group
  }

  for (const line of lines) {
    if (line.rooms.length === 0 && line.orderQty != null) {
      groupFor(null, "").entries.push(entryFor(line, null))
      continue
    }
    for (const detail of line.rooms) {
      groupFor(detail.rowId || null, detail.label).entries.push(entryFor(line, detail))
    }
  }

  // Schedule order first, unknowns in appearance order, the flat group last.
  const position = new Map(rowOrder.map((id, i) => [id, i]))
  const keys = order
    .filter((k) => k !== "__flat__")
    .sort((a, b) => {
      const pa = position.get(a) ?? rowOrder.length + order.indexOf(a)
      const pb = position.get(b) ?? rowOrder.length + order.indexOf(b)
      return pa - pb
    })
  if (order.includes("__flat__")) keys.push("__flat__")
  return keys.map((k) => groups.get(k)!)
}

function entryFor(
  line: WorksheetLine,
  detail: WorksheetRoomDetail | null
): WorksheetRoomGroup["entries"][number] {
  return {
    key: line.key,
    kind: line.kind,
    label: line.label,
    // A typed detail in its own unit keeps it through the pivot too.
    unit: detail?.unit ?? line.unit,
    netQty: detail?.netQty ?? null,
    orderQty: detail ? detail.orderQty : line.orderQty,
    mode: detail?.mode ?? null,
    manual: detail?.manual ?? line.manual,
  }
}

export type WorksheetTotals = { sf: number; sy: number; lf: number; cartons: number; rolls: number }

/**
 * Per-unit footer totals — Σ SF, Σ SY, Σ LF, Σ cartons, Σ rolls. EACH-class
 * lines are deliberately never totaled across items: 3 pails + 4 thresholds
 * is not a number. And no money, anywhere.
 */
export function worksheetTotals(lines: WorksheetLine[]): WorksheetTotals {
  const totals: WorksheetTotals = { sf: 0, sy: 0, lf: 0, cartons: 0, rolls: 0 }
  for (const line of lines) {
    if (line.orderQty != null) {
      if (line.unit === "SF") totals.sf += line.orderQty
      else if (line.unit === "SY") totals.sy += line.orderQty
      else if (line.unit === "LF") totals.lf += line.orderQty
    }
    // Roll goods order by the nested linear feet — a second quantity beside
    // their SY/SF area, and the one purchasing actually phones in.
    if (line.lf != null) totals.lf += line.lf
    if (line.cartons != null) totals.cartons += line.cartons
    if (line.rolls != null) totals.rolls += line.rolls
  }
  return totals
}
